import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heading, Text } from "@nbs/ui";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonBlock } from "@/components/Skeleton";
import { ReviewErrorState } from "@/features/review/components/ReviewErrorState";
import { useSessionExpiryRedirect } from "@/features/review/hooks/useSessionExpiryRedirect";
import { N2IndicateurDetail } from "../components/N2IndicateurDetail";
import { useN2Indicateur } from "../hooks/useN2Indicateur";

export interface N2TargetPageProps {
  /** Already parsed and normalized by `parseIndicateurIdParam` — never empty (see `N2Page`). */
  readonly indicateurId: string;
}

/**
 * The "`indicateurId` present" branch of `/n2` (§4.3–§4.5). `N2Page` (the
 * route scaffold) is the only caller, and only once
 * `parseIndicateurIdParam(searchParams.get("indicateurId"))` returned a
 * non-null id — the bare-`/n2` scaffold behaviour is untouched (see
 * `N2Page.tsx`).
 *
 * Four real states, in priority order: loading → auth error (reused
 * `ReviewErrorState`/`isAuthErrorCode`, §4.5) → generic GraphQL/network
 * error (same component, non-auth code) → not found (`EmptyState`, §4.4)
 * → resolved (`N2IndicateurDetail`). Mirrors `ReviewDetailPage`'s exact
 * branch ordering and its reused `useSessionExpiryRedirect` hook — no new
 * session-expiry logic here.
 */
export function N2TargetPage({ indicateurId }: N2TargetPageProps) {
  const { t } = useTranslation();
  const { indicateur, isInitialLoading, notFound, error, refetch } = useN2Indicateur(indicateurId);
  useSessionExpiryRedirect(error);

  return (
    <section className="nbs-n2-target-page nbs-detail-page" aria-labelledby="n2-target-heading">
      <header className="nbs-detail-page__header">
        <Link to="/n1" className="nbs-detail-page__back">
          {t("n2.target.backToCockpit")}
        </Link>
        <Heading level={1} id="n2-target-heading">
          {t("nav.n2")}
        </Heading>
        <Text tone="muted">{t("n2.target.description")}</Text>
      </header>

      {isInitialLoading ? (
        <>
          <Text tone="muted" role="status">
            {t("n2.target.loadingTitle")}
          </Text>
          <SkeletonBlock lines={4} />
        </>
      ) : error ? (
        <ReviewErrorState error={error} onRetry={() => void refetch()} />
      ) : notFound || !indicateur ? (
        <EmptyState title={t("n2.target.notFoundTitle")} body={t("n2.target.notFoundBody")} />
      ) : (
        <N2IndicateurDetail indicateur={indicateur} />
      )}
    </section>
  );
}
